/**
 * CASTLE ATLAS — AI Prompt Sandbox
 * ═════════════════════════════════════════════════════════════
 * Thin dispatcher: fetches a rep from the backend, then renders one of
 * the six format components based on rep.format. Each format component
 * owns its own work surface but renders through the shared SandboxShell
 * (rail + work-area order), useRunState (state machine), EvaluationTrace,
 * and ResultPanel — see the sandbox rebuild spec's Part 2/6/8. This file
 * only supplies what's common to all six: which rep/lesson is loaded, the
 * top nav strip, and where "continue" goes next.
 */

import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageTitle } from '@hooks/usePageTitle';
import { useForceSurface } from '@hooks/useSurface';
import { useRep } from '@hooks/useRep';
import { useReps } from '@hooks/useReps';
import { useLessons, lessonName } from '@hooks/useLessons';
import { logRepOpen } from '@services/repService';
import { TT, PHASES } from '@features/ai-prompting/terminalTokens';
import { SkeletonLoader } from '@features/ai-prompting/components/Term';
import StudyPanel from '@features/ai-prompting/components/StudyPanel';
import RepEditor from '@features/ai-prompting/components/RepEditor';
import ComparePane from '@features/ai-prompting/components/ComparePane';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

// rep.format -> component. `study` and `compare` are structurally
// different enough to need their own surface; complete/repair/build/
// transfer share RepEditor, entirely config/format-driven.
const FORMAT_COMPONENTS = {
  study: StudyPanel,
  compare: ComparePane,
  complete: RepEditor,
  repair: RepEditor,
  build: RepEditor,
  transfer: RepEditor,
};

const PromptSandbox = () => {
  useForceSurface('dark');
  const { skillSlug = 'ai-prompting', lessonId, position } = useParams();
  const navigate = useNavigate();

  const lessonIdNum = Number(lessonId);
  const positionNum = Number(position);

  const { rep, loading, error } = useRep(lessonIdNum, positionNum);
  const { reps: lessonReps } = useReps(lessonIdNum);
  const { lessons } = useLessons();

  usePageTitle(rep ? `${rep.title} — Lesson ${lessonId}` : 'Rep');

  // Health metrics need "opened but never submitted" and open -> first-
  // submit elapsed, neither derivable from prompt_executions alone (see
  // migrations/026_proof_plan.sql). Repeat opens are intentionally not
  // deduped — see rep_opens' own schema comment.
  useEffect(() => {
    if (rep?.rep_id) logRepOpen(rep.rep_id);
  }, [rep?.rep_id]);

  const currentIndex = lessonReps.findIndex(r => r.rep_id === rep?.rep_id);
  const totalReps = lessonReps.length;
  const isFirst = currentIndex <= 0;
  const isLast = currentIndex >= 0 && currentIndex === totalReps - 1;

  const goToPosition = (pos) => navigate(`/skills/${skillSlug}/lessons/${lessonId}/reps/${pos}`);
  const handleContinue = () => {
    if (!isLast) goToPosition(lessonReps[currentIndex + 1].position);
    else navigate(`/skills/${skillSlug}`);
  };

  const phase = rep ? PHASES.find(p => p.lessons.includes(lessonIdNum)) : null;
  const accent = phase?.color ?? TT.orange;
  const lesson = lessons.find(l => l.id === lessonIdNum);

  if (loading) return <SkeletonLoader loadingText="rep.sandbox" />;

  if (error || !rep) {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: TT.bg,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: TT.fontMono, gap: 12, padding: 24, textAlign: 'center',
      }}>
        <div style={{ color: '#ef4444', fontSize: 14 }}>{'>'} error: rep not found</div>
        <div style={{ color: TT.textDim, fontSize: 12, maxWidth: 360 }}>
          {error || `Lesson ${lessonId}, position ${position} doesn't exist.`}
        </div>
        <Link to={`/skills/${skillSlug}`} className="ps-focusable" style={{
          marginTop: 8, color: TT.orange, fontSize: 13, textDecoration: 'none',
          border: `1px solid ${TT.orange}`, borderRadius: TT.radius.sm, padding: '8px 16px',
        }}>
          ./back --to-map
        </Link>
      </div>
    );
  }

  const FormatComponent = FORMAT_COMPONENTS[rep.format];

  const topBar = (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      flexWrap: 'wrap', padding: '10px 22px', borderBottom: `1px solid ${TT.border}`,
    }}>
      <style>{`.ps-focusable:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => navigate(`/skills/${skillSlug}`)}
          className="ps-focusable"
          aria-label="Back to learning path"
          style={{ width: 24, height: 24, borderRadius: TT.radius.sm, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: `1px solid ${TT.border}`, color: TT.textMuted, cursor: 'pointer' }}
        >
          <ArrowLeft size={13} />
        </button>
        <button
          onClick={() => !isFirst && goToPosition(lessonReps[currentIndex - 1].position)}
          disabled={isFirst}
          className="ps-focusable"
          aria-label="Previous rep"
          style={{ width: 24, height: 24, borderRadius: TT.radius.sm, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: `1px solid ${TT.border}`, color: TT.textMuted, cursor: isFirst ? 'not-allowed' : 'pointer', opacity: isFirst ? 0.35 : 1 }}
        >
          <ChevronLeft size={13} />
        </button>
        <span style={{ fontFamily: TT.fontMono, fontSize: 11, color: TT.textDim, padding: '0 4px' }}>
          rep {currentIndex >= 0 ? currentIndex + 1 : '?'} of {totalReps || '?'}
        </span>
        <button
          onClick={() => !isLast && goToPosition(lessonReps[currentIndex + 1].position)}
          disabled={isLast}
          className="ps-focusable"
          aria-label="Next rep"
          style={{ width: 24, height: 24, borderRadius: TT.radius.sm, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: `1px solid ${TT.border}`, color: TT.textMuted, cursor: isLast ? 'not-allowed' : 'pointer', opacity: isLast ? 0.35 : 1 }}
        >
          <ChevronRight size={13} />
        </button>
      </div>

      <span style={{ fontFamily: TT.fontMono, fontSize: 11, color: TT.textMuted }}>
        {lessonName(lessons, lessonIdNum)} · Phase {phase?.num ?? '—'}
      </span>
    </div>
  );

  if (!FormatComponent) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: TT.bg }}>
        {topBar}
        <div style={{ padding: 24, color: '#ef4444', fontFamily: TT.fontMono, fontSize: 12 }}>Unknown rep format: {rep.format}</div>
      </div>
    );
  }

  return (
    <FormatComponent
      rep={rep}
      lessonName={lessonName(lessons, lessonIdNum)}
      principle={lesson?.principle}
      topBar={topBar}
      accent={accent}
      onContinue={handleContinue}
    />
  );
};

export default PromptSandbox;
